<script setup>
import { ref, onMounted, computed } from 'vue';
import { Modal } from 'bootstrap';
import { QuillEditor } from '@vueup/vue-quill';
import { useStore } from 'vuex';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { NotesClient } from '@/services/NotesClient';
import { CoursesClient } from '@/services/CoursesClient';
import Swal from 'sweetalert2';

const props = defineProps({
  courseId: {
    type: String,
    required: true
  }
});
const quillEditor = ref(null);

// This can be called when the editor is ready
const handleEditorReady = (quill) => {
  editor.value = quill;
};

const emit = defineEmits(['saved']);
const courseClient = new CoursesClient();
const notesClient = new NotesClient();
const store = useStore();

// State
const notesModal = ref(null);
const notesContent = ref('');
const title = ref('');
const loading = ref(true);
const saving = ref(false);
const deleting = ref(false);
const lastUpdated = ref(null);
const editor = ref(null);

// Get user data from store
const userData = computed(() => store.state.userData);
const userId = computed(() => userData.value?.id || 10); // Fallback to 8 if no user ID
const authorName = computed(() => {
  return `${userData.value?.firstName || ''} ${userData.value?.lastName || ''}`.trim() || 'Autor desconocido';
});

// Editor configuration
const editorOptions = {
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean']
    ]
  },
  placeholder: 'Escribe tus notas aquí...',
  bounds: '#notesModal .modal-body'
};

// Initialize modal
onMounted(() => {
  notesModal.value = new Modal(document.getElementById('notesModal'));
});

// Load notes from API
const loadNotes = async () => {
  try {
    loading.value = true;
    const response1 = await courseClient.getCourseById(props.courseId); 
    const response = await notesClient.getNote(props.courseId);

    title.value = response1.titulo || '';
    // Check if the response indicates a note exists
    if (response && response.contenido_nota !== null && !response.mensaje) {
      // Successful response with note data
      notesContent.value = response.contenido_nota || '';
      lastUpdated.value = response.fecha_actualizacion || new Date().toISOString();
    } else {
      // Either empty response or response indicating no note exists
      notesContent.value = '';
      lastUpdated.value = null;
    }
    console.log(notesContent.value, lastUpdated.value);
    
  } catch (error) {
    if (error.response?.status === 500 || error.response?.status === 404) {
      // Handle errors which mean no note exists
      notesContent.value = '';
      lastUpdated.value = null;
    }
    else {
      console.error('Error loading notes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar las notas. Por favor intenta nuevamente.'
      });
    }
  } finally {
    loading.value = false;
  }
};

// Save notes to API
const saveNotes = async () => {
  if (!notesContent.value.trim()) return;

  try {
    saving.value = true;
    
    // First try to load the current note to check if it exists
    const currentNote = await notesClient.getNote(props.courseId);
    
    if (currentNote && currentNote.contenido_nota !== null && !currentNote.mensaje) {
      // Note exists - update it (only send content)
      const updateData = {
        contenido_nota: notesContent.value
      };
      await notesClient.updateNote(props.courseId, updateData);
    } else {
      // Note doesn't exist - create new one (send both content and course_id)
      const createData = {
        contenido_nota: notesContent.value,
        curso_id: props.courseId
      };
      await notesClient.createNote(createData);
    }
    
    // Update the last updated time
    lastUpdated.value = new Date().toISOString();

    Swal.fire({
      icon: 'success',
      title: 'Notas guardadas',
      text: 'Tus notas se han guardado correctamente',
      timer: 2000,
      showConfirmButton: false
    });

    emit('saved');
  } catch (error) {
    console.error('Error saving notes:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudo guardar las notas. Por favor intenta nuevamente.'
    });
  } finally {
    saving.value = false;
  }
};

// Delete notes
const deleteNotes = async () => {
  const result = await Swal.fire({
    title: '¿Eliminar notas?',
    text: 'Esta acción no se puede deshacer',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  });

  if (result.isConfirmed) {
    try {
      deleting.value = true;
      await notesClient.deleteNote(props.courseId).catch(e => {
        if (!e.message.includes('JSON')) throw e;
      });

      // Clear both the reactive data and editor content
      notesContent.value = '';
      lastUpdated.value = null;
      
      // Access the Quill instance and clear its content
      if (editor.value) {
        quillEditor.value.setContents([]); // This clears the editor
      }

      Swal.fire({
        icon: 'success',
        title: 'Notas eliminadas',
        text: 'Tus notas se han eliminado correctamente',
        timer: 2000,
        showConfirmButton: false
      });

      emit('saved');
    } catch (error) {
      console.error('Error deleting notes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron eliminar las notas. Por favor intenta nuevamente.'
      });
    } finally {
      deleting.value = false;
    }
  }
};

// Format date for display
const formattedDate = computed(() => {
  if (!lastUpdated.value) return '-';

  const date = new Date(lastUpdated.value);
  return date.toLocaleString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Optional: 24-hour format
    timeZone: 'America/Costa_Rica' // Optional: set your local time zone
  });
});


// Expose show method
const show = () => {
  loadNotes();
  notesModal.value.show();
};

defineExpose({ show });
</script>

<template>
  <div class="modal fade" id="notesModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="fas fa-pencil-alt pe-2"></i> Mis Notas</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-sm">Curso: {{ title }}</h6>
          <button v-if="notesContent" class="btn btn-sm btn-outline-danger" @click="deleteNotes" :disabled="deleting">
            <span v-if="deleting" class="spinner-border spinner-border-sm"></span>
            Eliminar Notas
          </button>
          </div>       
        
          <div v-if="loading" class="text-center py-9">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Cargando...</span>
            </div>
          </div>
          <QuillEditor v-else   ref="quillEditor"
           theme="snow" v-model:content="notesContent" content-type="html" :options="editorOptions"
            @ready="handleEditorReady" />
        </div>

        <div class="modal-footer d-flex justify-content-between align-items-center">
          <div>
            <h6 class="text-xs me-4 text-start mb-0">Autor: {{ authorName }}</h6>
        
          </div>

          <div class="d-flex align-items-center">
            <h6 class="text-xs me-4 mb-0">Última actualización: {{ formattedDate }}</h6>
            <button type="button" class="btn btn-primary m-0" @click="saveNotes"
              :disabled="saving || !notesContent.trim()">
              <span v-if="saving" class="spinner-border spinner-border-sm"></span>
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Your existing styles remain the same */
.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #ff0000;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.btn-close::before {
  content: "×";
  color: #ff0000;
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
}

/* Editor styles */
:deep(.ql-toolbar) {
  border-radius: 0.375rem 0.375rem 0 0;
  border: 1px solid #dee2e6;
  border-bottom: none;
}

:deep(.ql-container) {
  border-radius: 0 0 0.375rem 0.375rem;
  border: 1px solid #dee2e6;
  height: 450px;
}

:deep(.ql-editor) {
  font-size: 1rem;
  line-height: 1.5;
}

:deep(.ql-editor.ql-blank::before) {
  color: #6c757d;
  font-style: normal;
}
</style>